import React, { useEffect, useMemo, useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import InventorySelectorModal from '@components/shared/selectors/InventorySelectorModal';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import { stockAdjustmentService } from '@services/operator/stockAdjustmentService';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { Button, Card, Form, Input, message, Space, Spin, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router-dom';

import AdjustmentItemModal from './components/AdjustmentItemModal';
import { getStockAdjustmentItemColumns } from './columns';

const { Title } = Typography;

interface AdjustmentItemPayload {
  id?: string;
  inventory_id: string;
  shelf_id: string;
  quantity: number;
  reason_code?: string | null;
  note?: string | null;
  product_sku?: string | null;
  product_name?: string | null;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  inventory_type_id?: string | null;
  inventory_type_name?: string | null;
  shelf_location?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  [key: string]: unknown;
}

interface StockAdjustmentLoaderData {
  id: string;
  name?: string | null;
  note?: string | null;
  // top-level merchant_id added by BE show endpoint for FE record-sync
  merchant_id?: string | null;
  merchant?: { id?: string | null; name?: string | null } | null;
  items: AdjustmentItemPayload[];
  [key: string]: unknown;
}

interface MappedItem extends Record<string, unknown> {
  tempId: string | number;
  inventory_id: string;
  shelf_id: string;
  quantity: number;
  reason_code?: string | null;
  note?: string | null;
  inventory: {
    id: string;
    product_sku?: string | null;
    product_name?: string | null;
    batch?: string | null;
    expiration_date?: string | null;
    manufacturing_date?: string | null;
    inventory_type?: string | null;
    inventory_type_id?: string | null;
    custom_attributes?: Record<string, unknown> | null;
  };
  shelf: {
    shelf_id: string;
    shelf_location?: string | null;
    shelf_code?: string | null;
  };
}

interface StockAdjustmentEditContentProps {
  // merchantId 由 wrapper 對齊到 record's merchant 後傳入；用於 merchant-scoped
  // 下拉資料（inventory attribute definitions、InventorySelectorModal 等）。
  merchantId: string | null;
  // True while wrapper is still switching OperatorDataContext.currentMerchant
  // to the record's merchant. Render-gated to avoid mismatched dropdowns.
  merchantDataLoading?: boolean;
  // Called after the loader data is available so the wrapper can sync context.
  onAdjustmentLoaded?: (adjustment: StockAdjustmentLoaderData) => void;
}

function StockAdjustmentEditContent({
  merchantId,
  merchantDataLoading = false,
  onAdjustmentLoaded,
}: StockAdjustmentEditContentProps) {
  const adjustment = useLoaderData() as StockAdjustmentLoaderData;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const productService = useOperatorProductService();
  // merchantId 從 wrapper 傳入，已對齊到 record's merchant，避免 header 切換
  // 導致此 hook 拉到別的 merchant 的 attribute definitions。
  const attributeDefinitions = useInventoryAttributeDefinitions('operator', merchantId);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map(def => [def.name, def.label || def.name])),
    [attributeDefinitions],
  );
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<MappedItem[]>([]);

  // Modals
  const [selectorVisible, setSelectorVisible] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<Record<string, unknown> | null>(null);

  // 通知 wrapper：loader 已拿到 record，讓它把 currentMerchant 對齊到 record's merchant。
  // 用 adjustment?.id 當 deps key 確保只在 loader 換 record 時觸發一次。
  useEffect(() => {
    if (adjustment) {
      onAdjustmentLoaded?.(adjustment);
    }
  }, [adjustment, onAdjustmentLoaded]);

  // Initialize data
  useEffect(() => {
    if (adjustment) {
      form.setFieldsValue({
        name: adjustment.name,
        note: adjustment.note,
      });

      const mappedItems: MappedItem[] = (adjustment.items || []).map(item => ({
        tempId: item.id ?? `${Date.now()}-${Math.random()}`,
        inventory_id: item.inventory_id,
        shelf_id: item.shelf_id,
        quantity: item.quantity,
        reason_code: item.reason_code,
        note: item.note,

        inventory: {
          id: item.inventory_id,
          product_sku: item.product_sku,
          product_name: item.product_name,
          batch: item.batch,
          expiration_date: item.expiration_date,
          manufacturing_date: item.manufacturing_date,
          inventory_type: item.inventory_type_name,
          inventory_type_id: item.inventory_type_id,
          custom_attributes: item.custom_attributes,
        },
        shelf: {
          shelf_id: item.shelf_id,
          shelf_location: item.shelf_location,
          shelf_code: item.shelf_location,
        },
      }));

      setItems(mappedItems);
    }
  }, [adjustment, form]);

  const handleSelectInventory = (inventory: Record<string, unknown>) => {
    setSelectedInventory(inventory);
    setSelectorVisible(false);
    setItemModalVisible(true);
  };

  const handleConfirmItem = (itemDataOrArray: MappedItem | MappedItem[]) => {
    const newItems = Array.isArray(itemDataOrArray) ? itemDataOrArray : [itemDataOrArray];
    const now = Date.now();
    setItems(prev => [...prev, ...newItems.map((item, idx) => ({ ...item, tempId: `${now}-${idx}` }))]);
    setItemModalVisible(false);
    setSelectedInventory(null);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleNoteChange = (index: number, value: string) => {
    setItems(prev => prev.map((item, i) => (i === index ? { ...item, note: value } : item)));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (items.length === 0) {
        message.error(t('shared.stock_adjustment.error.no_items'));
        return;
      }

      setLoading(true);
      const payload = {
        ...values,
        items: items.map(item => ({
          inventory_id: item.inventory_id,
          shelf_id: item.shelf_id,
          quantity: item.quantity,
          reason_code: item.reason_code,
          note: item.note,
        })),
      };

      const response = await stockAdjustmentService.update(adjustment.id, payload);
      if (response.success) {
        message.success(t('common.message.update_success'));
        navigate('/stock_adjustments');
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.message.operation_failed') });
      }
    } catch (error) {
      console.error('[StockAdjustmentsEdit] submit failed:', error);
      handleNetworkError(error, { t });
    } finally {
      setLoading(false);
    }
  };

  const columns = getStockAdjustmentItemColumns(t, {
    onRemove: handleRemoveItem,
    onNoteChange: handleNoteChange,
    attributeLabelByName,
  });

  return (
    <Spin spinning={merchantDataLoading}>
      <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
        <PageHeader items={['stocks', 'stocks.adjustments', { label: adjustment?.name || t('shared.stock_adjustment.edit_title') }]} />

        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>{t('shared.stock_adjustment.edit_title')}</Title>
          <Space>
            <Button onClick={goBack}>{t('common.button.cancel')}</Button>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              {t('common.button.save')}
            </Button>
          </Space>
        </div>

        <Card title={t('shared.stock_adjustment.basic_info')} bordered={false} style={{ marginBottom: 16 }}>
          <Form form={form} layout="inline">
            <Form.Item label={t('models.stock_adjustment.name')} name="name">
              <Input style={{ width: 220 }} />
            </Form.Item>
            <Form.Item label={t('models.stock_adjustment.note')} name="note" style={{ flex: 1 }}>
              <Input.TextArea rows={1} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        </Card>

        <Card
          title={t('shared.stock_adjustment.items')}
          bordered={false}
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setSelectorVisible(true)}>
              {t('common.button.add')}
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={items}
            rowKey="tempId"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: t('shared.stock_adjustment.no_items') }}
          />
        </Card>

        <InventorySelectorModal
          visible={selectorVisible}
          onCancel={() => setSelectorVisible(false)}
          onSelect={handleSelectInventory}
          merchantId={merchantId}
          productService={productService}
        />

        <AdjustmentItemModal
          visible={itemModalVisible}
          inventory={selectedInventory ?? undefined}
          existingItems={items}
          defaultReasonCode={items.length > 0 ? (items[items.length - 1].reason_code ?? undefined) : undefined}
          onCancel={() => setItemModalVisible(false)}
          onConfirm={handleConfirmItem}
        />
      </div>
    </Spin>
  );
}

const StockAdjustmentEdit: React.FC = () => {
  // 編輯模式：record 已由 router loader 載入（StockAdjustmentLoaderData），
  // 透過 useRecordMerchantSync 把 OperatorDataContext.currentMerchant 對齊到
  // 該 record 的 merchant，避免 header 切到別的 merchant 時下拉資料錯位。
  //
  // currentMerchant 在 sync 完成後即為 record 的 merchant；merchantId 直接從
  // context 取（一致來源），merchantDataLoading=true 期間 render 會被 <Spin> gate。
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync();
  const { currentMerchant } = useOperatorData();
  const merchantId = currentMerchant?.id ?? null;

  return (
    <StockAdjustmentEditContent
      merchantId={merchantId}
      merchantDataLoading={merchantDataLoading}
      onAdjustmentLoaded={handleRecordLoaded}
    />
  );
};

// 維持 default export 為 wrapper，但 edit mode 不再用 <MerchantSelect>（那是 create-only
// 的 pattern；edit 模式的 merchant 由 record 本身決定，不應讓使用者選）。
// 若未來需要 new 模式仍走同一個 file，請另建 New.tsx wrapper 用 <MerchantSelect>。
export default StockAdjustmentEdit;
